import { ApolloLink, type Operation, type NextLink } from '@apollo/client';
import { getPageType, PAGE_TYPES } from 'utils/pageTypes';

const getDomainFromPageType = (): Nullable<string> => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  const pageType = getPageType(window.location.pathname);

  if (!pageType || pageType === PAGE_TYPES.UNKNOWN) {
    return PAGE_TYPES.UNKNOWN.toLowerCase();
  }

  return pageType.toLowerCase();
};

/**
 *
 * Adds the following telemetry headers to each operation:
 *  - x-gql-domain
 *  - x-gql-fetch-policy
 *  - x-gql-component
 *
 * The domain is determined in the following order:
 *   1. from context.domain
 *   2. from the current page's pathname via getPageType()
 *
 * The fetchPolicy is determined in the following order:
 *   1. from context.fetchPolicy
 *   2. 'cache-first' if not provided, since Apollo Client's default fetch policy is 'cache-first'
 *
 * The nextFetchPolicy is determined in the following order:
 *   1. from context.nextFetchPolicy
 *   2. 'cache-first' if not provided, since Apollo Client's default next fetch policy is 'cache-first'
 *
 *  The component is determined in the following order:
 *   1. from context.component
 *   2. unknown if not provided
 */
export const telemetryLink = new ApolloLink((operation: Operation, forward: NextLink) => {
  const ctx = operation.getContext();

  const telemetry = ctx.telemetry ?? {};

  const domain = telemetry.domain ?? getDomainFromPageType();
  const fetchPolicy = telemetry.fetchPolicy ?? 'cache-first';
  const nextFetchPolicy = telemetry.nextFetchPolicy ?? 'cache-first';
  const component = telemetry.component ?? 'unknown';

  const existingHeaders = ctx.headers ?? {};

  const nextHeaders: Record<string, string> = {
    ...existingHeaders,
  };

  nextHeaders['x-gql-domain'] = domain;
  nextHeaders['x-gql-fetch-policy'] = fetchPolicy;
  nextHeaders['x-gql-next-fetch-policy'] = nextFetchPolicy;
  nextHeaders['x-gql-component'] = component;

  operation.setContext((prev: any) => ({
    ...prev,
    headers: nextHeaders,
  }));

  return forward(operation);
});
